import { Injectable, inject } from '@angular/core';
import { SupabaseService } from 'app/core/supabase/supabase.service';

export interface CurrentAgentInfo {
    agentId: string | null;
    userId: string | null;
    isAdmin: boolean;
}

/**
 * Resolves the current authenticated user's agent record once per session.
 * Replaces the ad-hoc loadCurrentAgent() pattern that was duplicated across many components.
 */
@Injectable({ providedIn: 'root' })
export class CurrentAgentService {
    private _supabase = inject(SupabaseService);
    private _cached: CurrentAgentInfo | null = null;
    private _pending: Promise<CurrentAgentInfo> | null = null;

    constructor() {
        // Un cambio de sesion invalida el agente resuelto. Solo limpiamos el
        // cache (nada asincrono aqui: supabase-js se bloquea si se le llama
        // desde dentro de su propio callback de auth).
        this._supabase.onAuthStateChange((event) => {
            if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
                this.reset();
            }
        });
    }

    async get(): Promise<CurrentAgentInfo> {
        if (this._cached) return this._cached;
        if (this._pending) return this._pending;

        this._pending = this._resolve();
        try {
            const result = await this._pending;
            // Solo cacheamos una resolucion buena. Un cache negativo (fallo de
            // red al arrancar, sesion todavia a medias) dejaba al usuario sin
            // agentId el resto de la sesion, y cada insert que depende de
            // `assigned_agent_id` terminaba rebotando contra las policies de RLS.
            if (result.agentId) {
                this._cached = result;
            }
            return result;
        } finally {
            this._pending = null;
        }
    }

    /**
     * Convenience: return just the agent id (or null if the current user isn't an agent).
     */
    async getAgentId(): Promise<string | null> {
        return (await this.get()).agentId;
    }

    /**
     * Clear the cache. Call after sign-in/sign-out or impersonation changes.
     */
    reset(): void {
        this._cached = null;
        this._pending = null;
    }

    private async _resolve(): Promise<CurrentAgentInfo> {
        // `getSession()` lee del storage local (y refresca el token si toca).
        // `getUser()` sale a la red y devolvia null ante cualquier fallo puntual.
        const { data: { session } } = await this._supabase.client.auth.getSession();
        const user = session?.user ?? null;
        if (!user) {
            return { agentId: null, userId: null, isAdmin: false };
        }

        const { data: agent, error } = await this._supabase.client
            .from('agents')
            .select('id, is_admin')
            .eq('user_id', user.id)
            .maybeSingle();

        if (error || !agent) {
            return { agentId: null, userId: user.id, isAdmin: false };
        }

        return {
            agentId: agent.id,
            userId: user.id,
            isAdmin: agent.is_admin === true,
        };
    }
}
